/**
 * App-projection ↔ unified `config.model` core (ADR-0063 FE-3 slice B) — PURE,
 * unit-tested. The app-mode QueryBuilder canvas authors an app projection as the
 * SAME unified `config.model` a global dataset uses, so backend + frontend share one
 * stored shape. This module is the single spec↔edit-model seam:
 *
 *  - {@link appEditFromConfig} DUAL-READS a stored `config` — either a LEGACY
 *    `DatasetSpec` (base_table + `columns` + `sort` …) OR the unified app-flavour
 *    `config.model` (base_table + `select` + `order_by` …) — into ONE uniform
 *    {@link AppEditModel} the canvas holds.
 *  - {@link appEditToConfig} emits the MINIMAL app-flavour `config.model` the backend
 *    persists. It carries only `as`/`via`/`from` on joins (the governed compiler
 *    re-derives `table`/`on`/`source`, exactly like `spec_to_model`), so it round-trips
 *    through the backend `model_to_spec` dual-read.
 *
 * The join model is RELATION-FOLLOW (mirrors the backend `entity_relation` join): a
 * join `{as, via, from}` follows the relation column `via` on `from` (the primary or an
 * earlier join) to its target table, exposed under alias `as`. The ON is structural
 * (`<from>.<via> = <as>.id`) — NOT a drawn column pair — so the app canvas has no
 * arbitrary drag-ON (unlike the global federation canvas). No React here.
 */

/** A relation-follow join, exposed under `as`, from `from` (default `base`).
 *  - `direction="forward"` (default, belongs-to): `via` is a relation ON `from`'s table.
 *  - `direction="reverse"` (has-many): `via` is a relation ON the CHILD (`target` table
 *    id) pointing back to `from` — brings in the rows that reference the parent (e.g. a
 *    company's deals). LEFT either way, so a parent with no children keeps its row. */
export interface AppModelJoin {
  as: string;
  via: string;
  from: string;
  type: "inner" | "left";
  direction?: "forward" | "reverse";
  /** The CHILD table id — set for a reverse (has-many) join. */
  target?: string;
}

/** A projected source column, qualified by its relation alias (`base` = primary). */
export interface AppModelSelectSource {
  as: string;
  alias: string;
  field: string;
  label?: string;
}
/** A derived expression column (raw JS over the joined row, shared formulas.js). */
export interface AppModelSelectExpr {
  as: string;
  expression: string;
  result_type: string;
  label?: string;
}
export type AppModelSelect = AppModelSelectSource | AppModelSelectExpr;

export function isExprSelect(s: AppModelSelect): s is AppModelSelectExpr {
  return (s as AppModelSelectExpr).expression !== undefined;
}

/** A filter over an OUTPUT column key (`as`); value is a literal or `{ $param }`. */
export interface AppModelFilter {
  field: string;
  op: string;
  value?: unknown;
}
export interface AppModelGroup {
  field: string; // an output column key
}
export interface AppModelAgg {
  fn: string; // count · sum · avg · min · max
  as: string;
  field?: string; // omitted for count(*); else an output column key
}
export interface AppModelOrder {
  by: string; // an output column key / agg alias
  dir: "asc" | "desc";
}

/** One ON condition of a federation join: `left` is a PROJECTED output key of this
 *  projection, `right` a field of the LINKED dataset. */
export interface AppModelDatasetOn {
  left: string;
  right: string;
}
/** A linked-dataset field to project, under output key `as`. */
export interface AppModelDatasetColumn {
  field: string;
  as: string;
}
/** A FEDERATION join to a LINKED global Dataset (ADR-0063 P5-S2 followup, "part 2").
 *  Unlike a relation-follow {@link AppModelJoin} over app tables, this joins the
 *  projection to an external/global Dataset the app LINKED (`app_dataset_links`);
 *  the engine federates it + applies the role's N4 grants. */
export interface AppModelDatasetJoin {
  dataset_id: string;
  type: "inner" | "left";
  on: AppModelDatasetOn[];
  columns: AppModelDatasetColumn[];
}

/** One column of a UNION branch: the linked dataset's `field` supplies this
 *  projection's OUTPUT column `output` in the stacked result (ADR-0064 P5). */
export interface AppModelUnionColumn {
  output: string;
  field: string;
}
/** A UNION branch — STACK a LINKED global Dataset's rows under this projection,
 *  mapping its fields onto the projection's OUTPUT columns (ADR-0064 P5). Only a
 *  linked global Dataset is a valid branch (N4-governed); an app-table Entity branch
 *  would bypass the portal RLS, so it's not offered. */
export interface AppModelUnion {
  dataset_id: string;
  type: "union" | "union_all";
  columns: AppModelUnionColumn[];
}
/** An UNFOLD entry — EXPLODE a JSON-array OUTPUT column into rows (ADR-0064 P5
 *  Transformation): one row per element, an object element hoisting its keys. */
export interface AppModelUnfold {
  field: string;
  keep_empty?: boolean;
}

/** The uniform in-canvas edit model — a superset both stored shapes hydrate into. */
export interface AppEditModel {
  base_table: string;
  joins: AppModelJoin[];
  select: AppModelSelect[];
  filters: AppModelFilter[];
  match: "all" | "any";
  group_by: AppModelGroup[];
  aggregations: AppModelAgg[];
  order_by: AppModelOrder[];
  /** Federation joins to LINKED global datasets (part 2). */
  dataset_joins: AppModelDatasetJoin[];
  /** UNION branches (ADR-0064 P5) — stack a linked dataset's rows, N4-governed. */
  unions: AppModelUnion[];
  /** UNFOLD (ADR-0064 P5 Transformation) — explode JSON-array output columns. */
  unfold: AppModelUnfold[];
}

/** The primary relation's alias — its columns are unqualified in the physical table. */
export const BASE_ALIAS = "base";

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asStr = (v: unknown, dflt = ""): string => (typeof v === "string" ? v : dflt);

/**
 * DUAL-READ a stored `config` into the uniform edit model. Detects the shape by its
 * projection key: the unified app model uses `select`, a legacy `DatasetSpec` uses
 * `columns`. `baseTableId` is the fallback base when the config omits `base_table`
 * (a fresh/empty projection).
 */
export function appEditFromConfig(
  config: Record<string, unknown> | null | undefined,
  baseTableId: string,
): AppEditModel {
  const c = (config ?? {}) as Record<string, unknown>;
  const isModel = "select" in c; // unified app-flavour; legacy has `columns`
  const base = asStr(c.base_table, baseTableId) || baseTableId;

  const joins: AppModelJoin[] = asArray(c.joins).map((raw) => {
    const j = raw as Record<string, unknown>;
    const reverse = j.direction === "reverse";
    return {
      as: asStr(j.as ?? j.alias),
      via: asStr(j.via),
      from: asStr(j.from, BASE_ALIAS) || BASE_ALIAS,
      type: j.type === "inner" ? "inner" : "left",
      direction: reverse ? "reverse" : "forward",
      ...(reverse && (j.target_table || j.target)
        ? { target: asStr(j.target_table ?? j.target) }
        : {}),
    };
  });

  const select: AppModelSelect[] = isModel
    ? asArray(c.select).map((raw) => normalizeSelect(raw as Record<string, unknown>))
    : asArray(c.columns).map((raw) => normalizeLegacyColumn(raw as Record<string, unknown>));

  const filters: AppModelFilter[] = asArray(c.filters).map((raw) => {
    const f = raw as Record<string, unknown>;
    return {
      field: asStr(f.field ?? f.column),
      op: asStr(f.op, "eq") || "eq",
      ...("value" in f ? { value: f.value } : {}),
    };
  });

  const group_by: AppModelGroup[] = isModel
    ? asArray(c.group_by).map((raw) => ({ field: asStr((raw as Record<string, unknown>).field) }))
    : asArray(c.group_by).map((raw) => ({ field: asStr(raw) }));

  const aggregations: AppModelAgg[] = isModel
    ? asArray(c.aggregations).map((raw) => {
        const a = raw as Record<string, unknown>;
        return {
          fn: asStr(a.fn, "count") || "count",
          as: asStr(a.as),
          ...(a.field !== undefined ? { field: asStr(a.field) } : {}),
        };
      })
    : asArray(c.aggregates).map((raw) => {
        const a = raw as Record<string, unknown>;
        return {
          fn: asStr(a.op ?? a.fn, "count") || "count",
          as: asStr(a.key ?? a.as),
          ...(a.column !== undefined ? { field: asStr(a.column) } : {}),
        };
      });

  const order_by: AppModelOrder[] = isModel
    ? asArray(c.order_by).map((raw) => {
        const o = raw as Record<string, unknown>;
        return { by: asStr(o.by), dir: o.dir === "desc" ? "desc" : "asc" };
      })
    : asArray(c.sort).map((raw) => {
        const o = raw as Record<string, unknown>;
        return { by: asStr(o.column), dir: o.dir === "desc" ? "desc" : "asc" };
      });

  const dataset_joins: AppModelDatasetJoin[] = asArray(c.dataset_joins).map((raw) => {
    const j = raw as Record<string, unknown>;
    return {
      dataset_id: asStr(j.dataset_id),
      type: j.type === "inner" ? "inner" : "left",
      on: asArray(j.on).map((o) => {
        const oo = o as Record<string, unknown>;
        return { left: asStr(oo.left), right: asStr(oo.right) };
      }),
      columns: asArray(j.columns).map((cc) => {
        const c2 = cc as Record<string, unknown>;
        return { field: asStr(c2.field), as: asStr(c2.as) };
      }),
    };
  });

  const unions: AppModelUnion[] = asArray(c.unions).map((raw) => {
    const u = raw as Record<string, unknown>;
    return {
      dataset_id: asStr(u.dataset_id),
      type: u.type === "union" ? "union" : "union_all",
      columns: asArray(u.columns).map((cc) => {
        const c2 = cc as Record<string, unknown>;
        return { output: asStr(c2.output), field: asStr(c2.field) };
      }),
    };
  });

  const unfold: AppModelUnfold[] = asArray(c.unfold).map((raw) => {
    const u = raw as Record<string, unknown>;
    return { field: asStr(u.field), keep_empty: u.keep_empty !== false };
  });

  return {
    base_table: base,
    joins,
    select,
    filters,
    match: c.match === "any" ? "any" : "all",
    group_by,
    aggregations,
    order_by,
    dataset_joins,
    unions,
    unfold,
  };
}

function normalizeSelect(s: Record<string, unknown>): AppModelSelect {
  const as = asStr(s.as);
  const label = typeof s.label === "string" ? s.label : undefined;
  if ("expression" in s) {
    return { as, expression: asStr(s.expression), result_type: asStr(s.result_type, "text") || "text", ...(label ? { label } : {}) };
  }
  return { as, alias: asStr(s.alias, BASE_ALIAS) || BASE_ALIAS, field: asStr(s.field), ...(label ? { label } : {}) };
}

function normalizeLegacyColumn(col: Record<string, unknown>): AppModelSelect {
  const as = asStr(col.key);
  const label = typeof col.label === "string" ? col.label : undefined;
  const source = col.source as Record<string, unknown> | null | undefined;
  if (source && !col.expression) {
    return { as, alias: asStr(source.alias, BASE_ALIAS) || BASE_ALIAS, field: asStr(source.column), ...(label ? { label } : {}) };
  }
  return { as, expression: asStr(col.expression), result_type: asStr(col.result_type, "text") || "text", ...(label ? { label } : {}) };
}

/**
 * Emit the MINIMAL app-flavour `config.model` for persistence — only the keys the
 * governed `model_to_spec` reads back (`as`/`via`/`from` on joins; `select` refs; the
 * shape). Empty pieces are DROPPED, except `select`/`base_table` which are always
 * present so `is_app_model` routes it (and the backend's `columns` min-length holds).
 * A grouped/aggregated model drops `select` (the engine ignores it when grouping).
 */
export function appEditToConfig(m: AppEditModel): Record<string, unknown> {
  const grouping = m.group_by.length > 0 || m.aggregations.length > 0;
  const out: Record<string, unknown> = { base_table: m.base_table };

  // `select` is REQUIRED to route as an app model AND to satisfy the backend column
  // min-length — but when grouping the engine ignores it, so we still emit a (possibly
  // group-derived) select so the routing marker is present. The caller guarantees ≥1.
  out.select = m.select.map((s) =>
    isExprSelect(s)
      ? { as: s.as, expression: s.expression, result_type: s.result_type, ...(s.label ? { label: s.label } : {}) }
      : { as: s.as, alias: s.alias, field: s.field, ...(s.label ? { label: s.label } : {}) },
  );

  if (m.joins.length) {
    out.joins = m.joins.map((j) => ({
      as: j.as,
      via: j.via,
      from: j.from,
      type: j.type,
      // A reverse (has-many) join carries direction + the explicit child target so the
      // backend `model_to_spec` re-derives the flipped ON.
      ...(j.direction === "reverse" ? { direction: "reverse", target_table: j.target } : {}),
    }));
  }
  if (m.filters.length) {
    out.filters = m.filters.map((f) => ({ field: f.field, op: f.op, ...("value" in f ? { value: f.value } : {}) }));
    out.match = m.match;
  }
  if (m.group_by.length) out.group_by = m.group_by.map((g) => ({ field: g.field }));
  if (m.aggregations.length) {
    out.aggregations = m.aggregations.map((a) => ({ fn: a.fn, as: a.as, ...(a.field ? { field: a.field } : {}) }));
  }
  if (m.order_by.length) out.order_by = m.order_by.map((o) => ({ by: o.by, dir: o.dir }));
  // Federation joins to LINKED datasets (part 2). DROP an INCOMPLETE join (no full ON
  // pair) so an in-progress authoring never 422s the backend (which requires ≥1 ON) —
  // consistent with the "drop empty pieces" rule; likewise drop incomplete columns.
  const dj = m.dataset_joins
    .map((j) => ({
      dataset_id: j.dataset_id,
      type: j.type,
      on: j.on.filter((o) => o.left && o.right),
      columns: j.columns.filter((c) => c.field && c.as),
    }))
    .filter((j) => j.dataset_id && j.on.length > 0);
  if (dj.length) out.dataset_joins = dj;
  // UNION branches (ADR-0064 P5) — DROP an incomplete branch (no mapped column) so an
  // in-progress authoring never 422s the backend (which requires ≥1 column).
  const un = m.unions
    .map((u) => ({
      dataset_id: u.dataset_id,
      type: u.type,
      columns: u.columns.filter((c) => c.output && c.field),
    }))
    .filter((u) => u.dataset_id && u.columns.length > 0);
  if (un.length) out.unions = un;
  // UNFOLD (ADR-0064 P5 Transformation) — drop a blank-field entry.
  const uf = m.unfold.filter((u) => u.field.trim());
  if (uf.length) out.unfold = uf.map((u) => ({ field: u.field, keep_empty: u.keep_empty !== false }));
  void grouping;
  return out;
}

// --- Canvas helpers (relation-follow join graph) --------------------------------

/** The output key for a projected column: the field for the primary, `<as>_<field>`
 *  for a join — so the same column name on two relations doesn't collide. */
export function outputKey(alias: string, field: string): string {
  return alias === BASE_ALIAS ? field : `${alias}_${field}`;
}

/** Add a relation-follow join. `as` defaults to `via`, de-duplicated against existing
 *  aliases (`via`, `via_2`, …). A reverse (has-many) join passes `{direction:"reverse",
 *  target}` (the child table id). Returns the updated joins (never mutates). */
export function addRelationJoin(
  joins: AppModelJoin[],
  from: string,
  via: string,
  opts?: { direction?: "forward" | "reverse"; target?: string },
): AppModelJoin[] {
  const taken = new Set([BASE_ALIAS, ...joins.map((j) => j.as)]);
  let as = via;
  let n = 2;
  while (taken.has(as)) as = `${via}_${n++}`;
  const j: AppModelJoin = { as, via, from, type: "left" };
  if (opts?.direction === "reverse") {
    j.direction = "reverse";
    j.target = opts.target;
  }
  return [...joins, j];
}

/** Remove a join by alias, plus any LATER join that chained `from` it (dangling), plus
 *  any `select`/filter/group/sort that referenced a removed alias's columns. */
export function removeJoinCascade(m: AppEditModel, alias: string): AppEditModel {
  const removed = new Set<string>([alias]);
  // A join whose `from` is (transitively) removed is itself removed.
  let changed = true;
  while (changed) {
    changed = false;
    for (const j of m.joins) {
      if (!removed.has(j.as) && removed.has(j.from)) {
        removed.add(j.as);
        changed = true;
      }
    }
  }
  const joins = m.joins.filter((j) => !removed.has(j.as));
  const keptKeys = new Set(
    m.select.filter((s) => isExprSelect(s) || !removed.has(s.alias)).map((s) => s.as),
  );
  return {
    ...m,
    joins,
    select: m.select.filter((s) => isExprSelect(s) || !removed.has(s.alias)),
    filters: m.filters.filter((f) => keptKeys.has(f.field)),
    group_by: m.group_by.filter((g) => keptKeys.has(g.field)),
    order_by: m.order_by.filter((o) => keptKeys.has(o.by)),
    aggregations: m.aggregations.filter((a) => !a.field || keptKeys.has(a.field)),
  };
}

/** Toggle a source column in/out of the projection (DevExpress-style card checkbox). */
export function toggleSelectColumn(
  m: AppEditModel,
  alias: string,
  field: string,
): AppEditModel {
  const key = outputKey(alias, field);
  const present = m.select.some((s) => !isExprSelect(s) && s.alias === alias && s.field === field);
  if (present) {
    return {
      ...m,
      select: m.select.filter((s) => isExprSelect(s) || !(s.alias === alias && s.field === field)),
      filters: m.filters.filter((f) => f.field !== key),
      group_by: m.group_by.filter((g) => g.field !== key),
      order_by: m.order_by.filter((o) => o.by !== key),
      aggregations: m.aggregations.filter((a) => a.field !== key),
    };
  }
  return { ...m, select: [...m.select, { as: key, alias, field }] };
}

/** The set of a relation's fields currently projected (drives the card checkboxes). */
export function selectedFieldsFor(m: AppEditModel, alias: string): Set<string> {
  return new Set(
    m.select.filter((s): s is AppModelSelectSource => !isExprSelect(s) && s.alias === alias).map((s) => s.field),
  );
}

/** The output column keys available to filters/group/sort — the projected aliases, any
 *  aggregation aliases (so a grouped output can sort by its aggregate), AND the columns
 *  projected from FEDERATED (linked-dataset) joins (`dataset_joins[].columns[].as`). The
 *  federated columns appear in the result grid (`RUDT_CKY_CNT`, …) but were missing here,
 *  so the Raggruppa/Ordina/Filtri panels couldn't offer them (ADR-0064 slice 0). The
 *  backend resolves such an alias to a QUALIFIED `{dataset, field}` reference at query time. */
export function outputKeys(m: AppEditModel): string[] {
  return [
    ...m.select.map((s) => s.as),
    ...m.aggregations.map((a) => a.as),
    ...m.dataset_joins.flatMap((dj) => dj.columns.map((c) => c.as)),
  ];
}

// --- Federation (linked-dataset) helpers ----------------------------------------

/** The projected output keys usable as a federation ON `left` (v1: a projected
 *  source column — aggregation isn't combined with federation). */
export function federationLeftKeys(m: AppEditModel): string[] {
  return m.select.map((s) => s.as);
}

/** Add a federation join to a linked dataset (de-duplicated by id), seeded with one
 *  empty ON row so the canvas prompts for the key pair. Never mutates. */
export function addDatasetJoin(m: AppEditModel, datasetId: string): AppEditModel {
  if (!datasetId || m.dataset_joins.some((j) => j.dataset_id === datasetId)) return m;
  return {
    ...m,
    dataset_joins: [
      ...m.dataset_joins,
      { dataset_id: datasetId, type: "left", on: [{ left: "", right: "" }], columns: [] },
    ],
  };
}

/** Remove a federation join by dataset id. */
export function removeDatasetJoin(m: AppEditModel, datasetId: string): AppEditModel {
  return { ...m, dataset_joins: m.dataset_joins.filter((j) => j.dataset_id !== datasetId) };
}

/** Patch one federation join (type / on / columns). */
export function patchDatasetJoin(
  m: AppEditModel,
  datasetId: string,
  patch: Partial<AppModelDatasetJoin>,
): AppEditModel {
  return {
    ...m,
    dataset_joins: m.dataset_joins.map((j) => (j.dataset_id === datasetId ? { ...j, ...patch } : j)),
  };
}

/** Turn a display name + field into a safe output-key identifier (`^[A-Za-z_]\w*$`). */
export function federationColumnKey(datasetName: string, field: string): string {
  const raw = `${datasetName}_${field}`.replace(/[^A-Za-z0-9_]/g, "_");
  return /^[A-Za-z_]/.test(raw) ? raw : `f_${raw}`;
}

/** Toggle a linked-dataset field in/out of the projection, keyed by a safe output
 *  key derived from `datasetName`. */
export function toggleDatasetColumn(
  m: AppEditModel,
  datasetId: string,
  datasetName: string,
  field: string,
): AppEditModel {
  const join = m.dataset_joins.find((j) => j.dataset_id === datasetId);
  if (!join) return m;
  const present = join.columns.some((c) => c.field === field);
  const columns = present
    ? join.columns.filter((c) => c.field !== field)
    : [...join.columns, { field, as: federationColumnKey(datasetName, field) }];
  return patchDatasetJoin(m, datasetId, { columns });
}

// --- Union (stack a linked dataset) helpers -------------------------------------

/** Add a UNION branch to a linked dataset (de-duplicated by id), empty column map.
 *  `union_all` (keep all) is the default. Never mutates. */
export function addUnion(m: AppEditModel, datasetId: string): AppEditModel {
  if (!datasetId || m.unions.some((u) => u.dataset_id === datasetId)) return m;
  return {
    ...m,
    unions: [...m.unions, { dataset_id: datasetId, type: "union_all", columns: [] }],
  };
}

/** Remove a UNION branch by dataset id. */
export function removeUnion(m: AppEditModel, datasetId: string): AppEditModel {
  return { ...m, unions: m.unions.filter((u) => u.dataset_id !== datasetId) };
}

/** Flip a branch between `union` (dedup) and `union_all` (keep all). */
export function setUnionType(
  m: AppEditModel,
  datasetId: string,
  type: "union" | "union_all",
): AppEditModel {
  return {
    ...m,
    unions: m.unions.map((u) => (u.dataset_id === datasetId ? { ...u, type } : u)),
  };
}

/** Map (or, with `field=""`, CLEAR) the branch column that supplies one OUTPUT column.
 *  A cleared output column falls to NULL in the stack (the backend defaults it). */
export function setUnionColumn(
  m: AppEditModel,
  datasetId: string,
  output: string,
  field: string,
): AppEditModel {
  return {
    ...m,
    unions: m.unions.map((u) => {
      if (u.dataset_id !== datasetId) return u;
      const rest = u.columns.filter((c) => c.output !== output);
      return { ...u, columns: field ? [...rest, { output, field }] : rest };
    }),
  };
}

// --- Unfold (explode a JSON-array column) helpers -------------------------------

/** Add an UNFOLD entry for an output column (keep_empty defaults true), de-duped. */
export function addUnfold(m: AppEditModel, field: string): AppEditModel {
  const f = field.trim();
  if (!f || m.unfold.some((u) => u.field === f)) return m;
  return { ...m, unfold: [...m.unfold, { field: f, keep_empty: true }] };
}

/** Remove an UNFOLD entry by field. */
export function removeUnfold(m: AppEditModel, field: string): AppEditModel {
  return { ...m, unfold: m.unfold.filter((u) => u.field !== field) };
}

/** Flip an UNFOLD entry's `keep_empty` (keep an empty-array row once vs drop it). */
export function setUnfoldKeepEmpty(m: AppEditModel, field: string, keep: boolean): AppEditModel {
  return {
    ...m,
    unfold: m.unfold.map((u) => (u.field === field ? { ...u, keep_empty: keep } : u)),
  };
}

/** A minimal structural view of an app table for alias resolution — just the columns
 *  (kept structural so this pure module doesn't couple to the api-apps types). */
export interface RelResolverTable {
  columns: { name: string; kind: string; config?: Record<string, unknown> | null }[];
}

/**
 * Resolve each alias (`base` + every join) to its app-table id by following the join
 * chain's `via` relation column to its `target_table` — mirrors the backend
 * `resolve_alias_tables`. `null` when a relation can't be resolved (table not loaded /
 * a bad `via`). Pure so the canvas can build cards + the resolution can be tested.
 */
export function resolveAppAliasTables(
  model: AppEditModel,
  baseTableId: string,
  tableById: Record<string, RelResolverTable>,
): Record<string, string | null> {
  const map: Record<string, string | null> = { [BASE_ALIAS]: baseTableId };
  for (const j of model.joins) {
    if (j.direction === "reverse") {
      // Has-many: the child table is EXPLICIT (can't be derived from a relation on `from`).
      map[j.as] = j.target ?? null;
      continue;
    }
    const fromTid = map[j.from ?? BASE_ALIAS];
    const via = fromTid
      ? tableById[fromTid]?.columns.find((c) => c.name === j.via && c.kind === "relation")
      : undefined;
    map[j.as] = (via?.config?.target_table as string | undefined) ?? null;
  }
  return map;
}
